import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog } from "@/components/common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CircleNotch, Medal, PencilSimple, Trash, Plus, Wallet } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
const btnPrimary = "rounded-sm bg-primary text-primary-foreground hover:bg-primary/80";

const STATUS = {
  pending: { label: "Pendiente", cls: "border-amber-500/40 text-amber-500" },
  applied: { label: "Pagada en planilla", cls: "border-emerald-500/40 text-emerald-500" },
};

export default function Comisiones() {
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ concept: "", amount: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    Promise.all([localApi.get("/commissions"), api.get("/employees")])
      .then(([c, e]) => {
        setItems(Array.isArray(c.data) ? c.data : []);
        setEmployees((Array.isArray(e.data) ? e.data : []).filter((x) => x.active));
      })
      .catch((err) => {
        toast.error(apiError(err));
        setItems([]);
      });
  }, []);

  useEffect(load, [load]);

  const byEmp = useMemo(() => {
    const map = {};
    (items || []).forEach((c) => {
      (map[c.employee_id] = map[c.employee_id] || []).push(c);
    });
    return map;
  }, [items]);

  const pendingOf = (empId) => (byEmp[empId] || []).filter((c) => c.status === "pending");
  const pendingTotalOf = (empId) => pendingOf(empId).reduce((s, c) => s + (c.amount || 0), 0);
  const appliedOf = (empId) => (byEmp[empId] || []).filter((c) => c.status === "applied");
  const appliedTotalOf = (empId) => appliedOf(empId).reduce((s, c) => s + (c.amount || 0), 0);

  const openManage = (emp) => {
    setTarget(emp);
    setForm({ concept: "", amount: "" });
    setEditing(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true);
    const payload = {
      employee_id: target.id,
      employee_name: target.full_name || "",
      employee_code: target.employee_code || "",
      concept: form.concept,
      amount: Number(form.amount) || 0,
    };
    try {
      if (editing) {
        await localApi.patch(`/commissions/${editing.id}`, payload);
        toast.success("Comisión actualizada");
      } else {
        await localApi.post("/commissions", payload);
        toast.success("Comisión agregada al acumulado del período");
      }
      setForm({ concept: "", amount: "" });
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await localApi.delete(`/commissions/${toDelete.id}`);
      toast.success("Comisión eliminada");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!items) return <Loading label="Cargando comisiones..." />;

  const totalPending = items.filter((c) => c.status === "pending").reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div data-testid="comisiones-page">
      <PageHeader
        title="Comisiones"
        description={`Acumulado pendiente de pagar en planillas: ${fmtMoney(totalPending)}`}
        testid="comisiones-header"
      />

      {employees.length === 0 ? (
        <EmptyState title="Sin empleados activos" description="Registra empleados primero para poder asignarles comisiones." />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm min-w-[820px]" data-testid="commissions-employees-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Empleado</th>
                <th className="p-3 font-normal">Comisiones del período</th>
                <th className="p-3 font-normal">Acumulado pendiente</th>
                <th className="p-3 font-normal">Pagadas (histórico)</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {employees.map((e) => {
                const pend = pendingOf(e.id);
                const pendTotal = pendingTotalOf(e.id);
                return (
                  <tr key={e.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`comm-emp-row-${e.employee_code}`}>
                    <td className="p-3">
                      <p className="text-foreground font-medium">{e.full_name}</p>
                      <p className="text-xs text-muted-foreground">{e.employee_code}</p>
                    </td>
                    <td className="p-3 text-foreground/80">{pend.length} comisión(es)</td>
                    <td className="p-3">
                      {pendTotal > 0 ? (
                        <span className="text-amber-500 font-medium" data-testid={`comm-pending-total-${e.employee_code}`}>{fmtMoney(pendTotal)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-foreground/80">{appliedOf(e.id).length > 0 ? fmtMoney(appliedTotalOf(e.id)) : "—"}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" className={btnPrimary} data-testid={`comm-manage-${e.employee_code}`} onClick={() => openManage(e)}>
                        <Wallet className="mr-1.5" size={14} /> Gestionar
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
        Las comisiones se van acumulando durante la semana, quincena o mes según cómo pagues. Al generar la planilla del período,
        el acumulado pendiente se suma automáticamente como bonificación y las comisiones se marcan como pagadas.
      </p>

      <Dialog open={!!target} onOpenChange={() => { setTarget(null); setEditing(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Medal size={18} className="text-primary" /> Comisiones — {target?.full_name}
            </DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-4" data-testid="comm-manage-dialog">
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 flex items-center justify-between" data-testid="comm-accumulated-banner">
                <span className="text-sm text-foreground/80">Acumulado pendiente de pagar en planilla</span>
                <span className="font-heading text-lg font-bold text-amber-500">{fmtMoney(pendingTotalOf(target.id))}</span>
              </div>

              <form onSubmit={save} className="border border-border rounded-sm p-3 space-y-3" data-testid="comm-add-form">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">{editing ? "Editar comisión" : "Agregar comisión al período"}</p>
                <div className="flex gap-2">
                  <Input className={`${inputCls} flex-1`} placeholder="Concepto (ej. ventas, meta cumplida)" required data-testid="comm-add-concept"
                    value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} />
                  <Input type="number" step="0.01" min="0" className={`${inputCls} w-32`} placeholder="Monto L" required data-testid="comm-add-amount"
                    value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving} className={btnPrimary} data-testid="comm-add-button">
                    {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <Plus className="mr-1.5" size={14} />}
                    {editing ? "Guardar cambios" : "Agregar"}
                  </Button>
                  {editing && (
                    <Button type="button" variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={() => { setEditing(null); setForm({ concept: "", amount: "" }); }} data-testid="comm-cancel-edit">
                      Cancelar edición
                    </Button>
                  )}
                </div>
              </form>

              <div className="border border-border rounded-sm divide-y divide-border" data-testid="comm-entries-list">
                {(byEmp[target.id] || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Este empleado aún no tiene comisiones.</p>
                ) : (
                  (byEmp[target.id] || []).map((c) => {
                    const st = STATUS[c.status] || STATUS.pending;
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-3 p-3" data-testid={`comm-row-${c.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{c.concept}</p>
                          <p className="text-[11px] text-muted-foreground">{fmtDate(c.status === "applied" ? c.applied_at : c.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm text-foreground font-medium">{fmtMoney(c.amount)}</span>
                          <Badge variant="outline" className={`rounded-sm font-normal ${st.cls}`} data-testid={`comm-status-${c.id}`}>{st.label}</Badge>
                          {c.status === "pending" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`comm-edit-${c.id}`}
                                onClick={() => { setEditing(c); setForm({ concept: c.concept, amount: c.amount }); }}>
                                <PencilSimple size={13} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`comm-delete-${c.id}`} onClick={() => setToDelete(c)}>
                                <Trash size={13} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}
        title="Eliminar comisión"
        description={`Se eliminará la comisión "${toDelete?.concept}" (${fmtMoney(toDelete?.amount)}) del acumulado.`}
        onConfirm={remove} testid="delete-commission" />
    </div>
  );
}
